// =============================================================================
// PARTNER IDENTITY SERVICE
// Single source of truth for partner identity resolution
// Handles UUID vs slug inconsistencies in one place
// =============================================================================

import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

const log = logger.module('PartnerIdentity');

// =============================================================================
// TYPES
// =============================================================================

export interface PartnerIdentity {
  uuid: string;           // Supabase UUID (primary key)
  slug: string;           // Legacy slug-based ID (e.g., 'choctaw-beach')
  name: string;           // Display name (e.g., 'Heith R.')
  location: string;       // City, State
}

export interface Partner {
  id: string;
  uuid?: string;
  legacyId?: string;
  name?: string;
  ownerName?: string;
  city?: string;
  state?: string;
  status?: string;
  globalAccess?: boolean;
  customRadius?: number;
  [key: string]: any;
}

// =============================================================================
// CACHE - Avoid repeated Supabase calls
// =============================================================================

let partnersCache: Partner[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL_MS = 60000; // 1 minute

async function getPartnersWithCache(): Promise<Partner[]> {
  const now = Date.now();
  if (partnersCache && (now - cacheTimestamp) < CACHE_TTL_MS) {
    return partnersCache;
  }

  try {
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .order('name');

    if (error) {
      log.error('Failed to fetch partners:', error);
      return partnersCache || [];
    }

    // Transform to app format
    partnersCache = (data || []).map(p => ({
      id: p.id,
      uuid: p.id,
      legacyId: p.legacy_id,
      name: p.name,
      ownerName: p.owner_name || p.name,
      city: p.city,
      state: p.state,
      status: p.status || 'active',
      globalAccess: p.global_access || false,
      customRadius: p.custom_radius,
      email: p.email,
      phone: p.phone,
      region: p.region,
      territory: p.territory,
      specialties: p.specialties || [],
      notes: p.notes
    }));
    cacheTimestamp = now;

    return partnersCache;
  } catch (err) {
    log.error('Exception fetching partners:', err);
    return partnersCache || [];
  }
}

// =============================================================================
// IDENTITY RESOLUTION
// =============================================================================

/**
 * Get all possible identifiers for a partner
 * Used for matching orders to partners regardless of ID format
 */
export function getAllPartnerIdentifiers(partner: Partner | null): Set<string> {
  const identifiers = new Set<string>();
  if (!partner) return identifiers;

  // UUID from Supabase
  if (partner.uuid) identifiers.add(partner.uuid);
  
  // Primary ID (could be UUID or slug depending on source)
  if (partner.id) identifiers.add(partner.id);
  
  // Legacy slug-based ID
  if (partner.legacyId) identifiers.add(partner.legacyId);
  
  // Name-based identifiers
  if (partner.name) {
    identifiers.add(partner.name);
    // Generate slug from name
    const slug = partner.name.toLowerCase().replace(/\s+/g, '-');
    identifiers.add(slug);
  }
  
  if (partner.ownerName) {
    identifiers.add(partner.ownerName);
  }

  return identifiers;
}

/**
 * Check if an order is targeted to a specific partner
 */
export function isOrderTargetedToPartner(order: any, partner: Partner | null): boolean {
  // No target = open to all
  if (!order.targetPartnerId) return true;
  
  const partnerIdentifiers = getAllPartnerIdentifiers(partner);
  return partnerIdentifiers.has(order.targetPartnerId);
}

/**
 * Check if an order is claimed by a specific partner
 */
export function isOrderClaimedByPartner(order: any, partnerId: string): boolean {
  if (!partnerId) return false;
  
  // Check all claim fields
  if (order.claimedByPartner === partnerId) return true;
  if (order.claimedByPartnerLocation === partnerId) return true;
  
  return false;
}

/**
 * Resolve any partner identifier to full identity
 * Input can be: UUID, slug, name, or partial match
 */
export async function resolvePartnerIdentity(input: string): Promise<PartnerIdentity | null> {
  if (!input) return null;

  const partners = await getPartnersWithCache();
  
  // Try exact matches first
  for (const partner of partners) {
    if (partner.uuid === input || 
        partner.id === input || 
        partner.legacyId === input) {
      return {
        uuid: partner.uuid || partner.id,
        slug: partner.legacyId || partner.id,
        name: partner.ownerName || partner.name || '',
        location: `${partner.city || ''}, ${partner.state || ''}`.trim()
      };
    }
  }

  // Try name match
  const inputLower = input.toLowerCase();
  for (const partner of partners) {
    const nameLower = (partner.name || '').toLowerCase();
    const ownerLower = (partner.ownerName || '').toLowerCase();
    const slugFromName = nameLower.replace(/\s+/g, '-');
    
    if (nameLower === inputLower || 
        ownerLower === inputLower ||
        slugFromName === inputLower) {
      return {
        uuid: partner.uuid || partner.id,
        slug: partner.legacyId || slugFromName,
        name: partner.ownerName || partner.name || '',
        location: `${partner.city || ''}, ${partner.state || ''}`.trim()
      };
    }
  }

  log.warn('Could not resolve partner identity:', input);
  return null;
}

/**
 * Get partner by any identifier (UUID, slug, name)
 */
export async function getPartnerByAnyId(input: string): Promise<Partner | null> {
  if (!input) return null;

  const partners = await getPartnersWithCache();
  
  // Try exact matches
  for (const partner of partners) {
    if (partner.uuid === input || 
        partner.id === input || 
        partner.legacyId === input) {
      return partner;
    }
  }

  // Try name/slug match
  const inputLower = input.toLowerCase();
  for (const partner of partners) {
    const nameLower = (partner.name || '').toLowerCase();
    const slugFromName = nameLower.replace(/\s+/g, '-');
    
    if (nameLower === inputLower || slugFromName === inputLower) {
      return partner;
    }
  }

  return null;
}

/**
 * Enrich a partner object with UUID from Supabase
 * Used when partner is selected via PIN (which uses slug-based ID)
 */
export async function enrichPartnerWithUUID(partner: Partner): Promise<Partner> {
  if (!partner) return partner;

  // Already has different UUID than ID? Already enriched
  if (partner.uuid && partner.uuid !== partner.id) {
    return partner;
  }

  try {
    // Try to find by legacy_id (slug)
    const { data } = await supabase
      .from('partners')
      .select('id, legacy_id, name, owner_name, city, state')
      .eq('legacy_id', partner.id)
      .maybeSingle();

    if (data?.id) {
      log.info('Enriched partner with UUID:', partner.id, '->', data.id);
      return {
        ...partner,
        uuid: data.id,
        legacyId: partner.id,
        name: data.name || partner.name,
        ownerName: data.owner_name || partner.ownerName,
        city: data.city || partner.city,
        state: data.state || partner.state
      };
    }

    // Try by name match
    const { data: byName } = await supabase
      .from('partners')
      .select('id, legacy_id, name, owner_name, city, state')
      .or(`name.ilike.%${partner.id}%,owner_name.ilike.%${partner.id}%`)
      .maybeSingle();

    if (byName?.id) {
      log.info('Enriched partner with UUID (by name):', partner.id, '->', byName.id);
      return {
        ...partner,
        uuid: byName.id,
        legacyId: byName.legacy_id || partner.id
      };
    }

    return partner;
  } catch (e) {
    log.warn('Failed to enrich partner with UUID:', e);
    return partner;
  }
}

/**
 * Get all active partners from Supabase
 */
export async function getAllPartners(): Promise<Partner[]> {
  return getPartnersWithCache();
}

/**
 * Get active partners only
 */
export async function getActivePartners(): Promise<Partner[]> {
  const all = await getPartnersWithCache();
  return all.filter(p => p.status === 'active');
}

/**
 * Clear the partners cache (call after partner updates)
 */
export function clearPartnersCache(): void {
  partnersCache = null;
  cacheTimestamp = 0;
}

// =============================================================================
// EXPORTS
// =============================================================================

export const PartnerIdentityService = {
  getAllPartnerIdentifiers,
  isOrderTargetedToPartner,
  isOrderClaimedByPartner,
  resolvePartnerIdentity,
  getPartnerByAnyId,
  enrichPartnerWithUUID,
  getAllPartners,
  getActivePartners,
  clearPartnersCache
};

export default PartnerIdentityService;
